import { env } from "@/app/config/env";
import { getLaunchUrl, onAppUrlOpen, onPushTapped, toAppPath } from "@/app/platform";
import { adoptAuthToken } from "./auth-client";

/**
 * EVERY WAY INTO A SCREEN THAT IS NOT A TAP INSIDE THE APP, HANDLED IN ONE PLACE.
 *
 *   · a universal link (an https URL on our own domain)
 *   · a custom-scheme link, including the one a social provider returns through
 *   · a tapped push notification, whose payload carries the same `path`
 *
 * They are one mechanism as far as the app is concerned, and keeping them one is what stops
 * "the link works but the notification doesn't" — the classic split-brain bug here.
 *
 * THE COLD-START CASE IS SEPARATE AND EASY TO MISS: a link that LAUNCHES the app is not
 * delivered as an event, it is read from the launch URL. Handle only the event and links
 * work perfectly in testing (app already open) and never for a real user.
 */
export function bindDeepLinks(navigate: (path: string) => void): () => void {
  const handleUrl = (url: string) => {
    const path = toAppPath(url, env.webUrl);
    if (!path) return;

    // A provider handing back a session: adopt the token, then land on the app, never on a
    // URL with a credential in it.
    const parsed = new URL(url);
    const token = parsed.searchParams.get("token");
    if (path.startsWith("/auth") && token) {
      void adoptAuthToken(token).then(() => navigate("/"));
      return;
    }
    navigate(path);
  };

  void getLaunchUrl().then((url) => url && handleUrl(url));
  const unbindUrl = onAppUrlOpen(handleUrl);
  const unbindPush = onPushTapped((data) => {
    const path = typeof data.path === "string" ? data.path : null;
    if (path) navigate(path);
  });

  return () => {
    unbindUrl();
    unbindPush();
  };
}
